import { useState, useRef, useCallback, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import { useConfirmation } from "../context/ConfirmationContext";
import API_URL from "../config";

const STATUS = {
  IDLE: "idle",
  DRAGGING: "dragging",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

export default function Relatorio({ tipo, titulo, codigo  }) {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [fileName, setFileName] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [file, setFile] = useState(null);
  const [dados, setDados] = useState([]);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [periodo, setPeriodo] = useState("");
  const [pdfs, setPdfs] = useState([]);
  const [pdfPage, setPdfPage] = useState(1);
  const [activeTab, setActiveTab] = useState("upload");
  const PDF_POR_PAGINA = 5;
  const [showScrollTop, setShowScrollTop] = useState(false);
  const inputRef = useRef(null);
  const { token, usuario } = useAuth();
  const { notify } = useNotification();
  const { confirm } = useConfirmation();
  const podeUpload = usuario?.perfil === "admin" || usuario?.perfil === "financeiro";

  // Usuários sem permissão de upload iniciam direto na aba de arquivos
  useEffect(() => {
    if (!podeUpload) setActiveTab("pdfs");
  }, [podeUpload]);

  const formatarValor = (valor) => {
    const numero = parseFloat(String(valor).replace(/\s/g, "").replace(",", ".")) || 0;
    return numero.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleFile = (f) => {
      if (!f) return;
      const valid = f.name.endsWith(".xls") || f.name.endsWith(".xlsx");
      if (!valid) {
        setErrorMsg("Apenas arquivos .xls ou .xlsx são aceitos.");
        setStatus(STATUS.ERROR);
        return;
      }

      const partes = f.name.replace(".xlsx", "").replace(".xls", "").trim().split(" ");
      
      // Busca o código e período pelos padrões numéricos
      const codigoArquivo = partes.find((p) => /^\d{4}$/.test(p)) || "";
      const periodoArquivo = partes.find((p) => /^\d{6}$/.test(p)) || "";

      if (codigoArquivo !== codigo) {
        setErrorMsg(`Arquivo inválido para este relatório. Esperado código ${codigo}, encontrado ${codigoArquivo || "nenhum"}.`);
        setStatus(STATUS.ERROR);
        return;
      }

      setFile(f);
      setFileName(f.name);
      setStatus(STATUS.IDLE);
      setErrorMsg("");
      setDados([]);
      setPeriodo(periodoArquivo);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setStatus(STATUS.IDLE);
    handleFile(e.dataTransfer.files[0]);
  }, []);

  const onDragOver = (e) => { e.preventDefault(); setStatus(STATUS.DRAGGING); };
  const onDragLeave = () => setStatus(STATUS.IDLE);
  const onInputChange = (e) => handleFile(e.target.files[0]);

  const handleCarregar = async () => {
    if (!file) return;
    setStatus(STATUS.LOADING);
    setErrorMsg("");

    const formData = new FormData();
    formData.append("arquivo", file);
    formData.append("periodo", periodo);

    try {
      const res = await fetch(`${API_URL}/api/relatorio/${tipo}/preview`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setDados(json);
      setStatus(STATUS.SUCCESS);
      notify.success(`${json.length} registro(s) carregado(s) com sucesso!`);
    } catch (err) {
      const mensagem = "Erro ao carregar dados: " + err.message;
      setErrorMsg(mensagem);
      setStatus(STATUS.ERROR);
      notify.error(mensagem);
    }
  };

  const handleGerarPdf = async () => {
    if (!file) return;

    // Verificar se já existe PDF do mesmo período
    const pdfExistente = pdfs.find((pdf) => pdf.nome_arquivo.includes(periodo));
    
    if (pdfExistente) {
      const confirmar = await confirm(
        `Já existe um PDF para ${periodo}`,
        `Deseja substituir o arquivo existente?`,
        "Substituir"
      );
      
      if (!confirmar) {
        return; // Cancelar a operação
      }
      
      // Deletar o PDF existente antes de gerar o novo
      try {
        const res = await fetch(`${API_URL}/api/relatorio/pdfs/${pdfExistente.id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(await res.text());
        notify.info("PDF anterior removido");
      } catch (err) {
        const mensagem = "Erro ao remover PDF antigo: " + err.message;
        setErrorMsg(mensagem);
        setStatus(STATUS.ERROR);
        notify.error(mensagem);
        return;
      }
    }

    setGerandoPdf(true);

    const formData = new FormData();
    formData.append("arquivo", file);
    formData.append("periodo", periodo);

    try {
        const res = await fetch(`${API_URL}/api/relatorio/${tipo}/pdf`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tipo}_${periodo || "relatorio"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      notify.success("PDF gerado e baixado com sucesso!");
      carregarPdfs();
    } catch (err) {
      const mensagem = "Erro ao gerar PDF: " + err.message;
      setErrorMsg(mensagem);
      setStatus(STATUS.ERROR);
      notify.error(mensagem);
    } finally {
      setGerandoPdf(false);
    }
  };

  const carregarPdfs = async () => {
      try {
          const resPdfs = await fetch(`${API_URL}/api/relatorio/${tipo}/pdfs`, {
              headers: { Authorization: `Bearer ${token}` },
          });

          const jsonPdfs = resPdfs.ok ? await resPdfs.json() : [];
          const extrairPeriodo = (nome) => {
              const match = nome.match(/_(\d{2})(\d{4})\.pdf$/);
              return match ? match[2] + match[1] : nome; // YYYYMM
          };
          setPdfs(jsonPdfs.sort((a, b) =>
              extrairPeriodo(b.nome_arquivo).localeCompare(extrairPeriodo(a.nome_arquivo))
          ));
          setPdfPage(1);
      } catch {
          console.error("Erro ao listar PDFs.");
      }
  };

  const handleBaixarPdf = (id, nome) => {
    fetch(`${API_URL}/api/relatorio/pdfs/${id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = nome;
        a.click();
        URL.revokeObjectURL(url);
        notify.success("PDF baixado com sucesso!");
      });
  };

  const handleGerarOcorrencias = async (periodo) => {
    try {
      const res = await fetch(`${API_URL}/api/relatorio/${tipo}/ocorrencias/${periodo}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        try {
          const erro = await res.json();
          const mensagem = erro.mensagem || erro.erro || "Erro desconhecido";
          notify.error(mensagem);
        } catch {
          notify.error("Erro ao gerar ocorrências: " + res.statusText);
        }
        return;
      }

      const contentType = res.headers.get("content-type");
      
      // Se for JSON (mensagem de nenhuma ocorrência)
      if (contentType.includes("application/json")) {
        const resultado = await res.json();
        notify.info(resultado.mensagem || "Nenhuma ocorrência encontrada.");
        return;
      }

      // Se for PDF
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ocorrencias_${tipo}_${periodo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      notify.success("Ocorrência baixada com sucesso!");
    } catch (err) {
      notify.error("Erro ao gerar ocorrências: " + err.message);
    }
  };

  const handleDeletarPdf = async (id) => {
    const confirmed = await confirm(
      "Deseja remover este PDF?",
      "Esta ação não pode ser desfeita.",
      "Remover"
    );
    if (!confirmed) return;
    try {
      const res = await fetch(`${API_URL}/api/relatorio/pdfs/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      notify.success("PDF removido com sucesso!");
      carregarPdfs();
    } catch (err) {
      notify.error("Erro ao deletar: " + err.message);
    }
  };

 useEffect(() => {
    carregarPdfs();
    setDados([]);
    setFile(null);
    setFileName(null);
    setStatus(STATUS.IDLE);
    setErrorMsg("");
    setPeriodo("");
    if (inputRef.current) inputRef.current.value = "";
}, [tipo]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const reset = () => {
    setStatus(STATUS.IDLE);
    setFile(null);
    setFileName(null);
    setErrorMsg("");
    setDados([]);
    setPeriodo("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "pdfs") {
      reset();
    }
  };

  const isDragging = status === STATUS.DRAGGING;
  const isLoading = status === STATUS.LOADING;
  const isError = status === STATUS.ERROR;

  const total = dados.reduce((acc, row) => {
    const valor = parseFloat((row.valor || "0").replace(",", ".")) || 0;
    return acc + valor;
  }, 0);

  const formatarData = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const podeExcluir = usuario?.perfil === "admin" || usuario?.perfil === "financeiro";

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col gap-6 py-6 px-4">

      {/* Header */}
      <div className="border-b-2 border-blue-600 pb-3">
        <h1 className="text-2xl font-bold text-gray-800">Relatório {titulo}</h1>
      </div>

      {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex border-b border-gray-100">
            {podeUpload && (
              <button
                onClick={() => setActiveTab("upload")}
                className={`flex-1 px-6 py-3 text-sm font-semibold transition-colors ${
                  activeTab === "upload"
                    ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                📤 Carregar & Gerar PDF
              </button>
            )}
            <button
              onClick={() => handleTabChange("pdfs")}
              className={`flex-1 px-6 py-3 text-sm font-semibold transition-colors ${
                activeTab === "pdfs"
                  ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              📁 Arquivos Gerados ({pdfs.length})
            </button>
          </div>

          {/* Aba 1: Upload */}
          {activeTab === "upload" && (
            <div className="p-6 flex flex-col gap-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Arquivo XLS</p>

              <div
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                  ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-gray-50 hover:border-blue-400 hover:bg-blue-50"}`}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xls,.xlsx"
                  onChange={onInputChange}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                />
                <div className="text-3xl mb-2">📂</div>
                {!fileName ? (
                  <>
                    <p className="text-sm text-gray-500">
                      <span className="font-semibold text-blue-600">Arraste o arquivo aqui</span> ou clique para selecionar
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Apenas .xls / .xlsx</p>
                  </>
                ) : (
                  <p className="text-sm font-semibold text-blue-600">📄 {fileName}</p>
                )}
              </div>

              <div className="flex gap-3">
                <button onClick={handleCarregar} disabled={!file || isLoading}
                  className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors">
                  {isLoading ? "Carregando..." : "Carregar Dados"}
                </button>

                {dados.length > 0 && (
                  <button onClick={handleGerarPdf} disabled={gerandoPdf}
                    className="flex-1 h-10 bg-green-600 hover:bg-green-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors">
                    {gerandoPdf ? "Gerando PDF..." : "⬇ Gerar PDF"}
                  </button>
                )}

                {(fileName || isError) && (
                  <button onClick={reset}
                    className="h-10 px-5 border border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 text-sm font-semibold rounded-lg transition-colors">
                    Limpar
                  </button>
                )}
              </div>

              {isError && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  <span>⚠</span> {errorMsg}
                </div>
              )}

              {/* Tabela prévia */}
              {dados.length > 0 && (
                <div className="mt-6 bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
                  <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white">
                    <p className="text-sm font-semibold text-gray-700">
                      Prévia <span className="text-gray-500 font-normal">({dados.length} registro{dados.length !== 1 ? "s" : ""})</span>
                    </p>
                    <span className="text-sm font-bold text-green-700">
                      R$ {total.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                          <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Matrícula</th>
                          <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Nome</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dados.map((row, i) => (
                          <tr key={i} className={`border-b border-gray-50 hover:bg-blue-50 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                            <td className="px-6 py-3 font-mono text-gray-500">{row.matricula}</td>
                            <td className="px-6 py-3 text-gray-700">{row.nome}</td>
                            <td className="px-6 py-3 text-right font-semibold text-green-600">R$ {formatarValor(row.valor)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-blue-100 bg-blue-50">
                          <td className="px-6 py-3 font-bold text-gray-700 uppercase text-xs tracking-wide">Total Geral</td>
                          <td className="px-6 py-3 text-gray-700">{dados.length} registro{dados.length !== 1 ? "s" : ""}</td>
                          <td className="px-6 py-3 text-right font-bold text-green-700">
                            R$ {total.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Aba 2: PDFs Salvos */}
          {activeTab === "pdfs" && (() => {
            const totalPaginas = Math.ceil(pdfs.length / PDF_POR_PAGINA);
            const pdfsVisiveis = pdfs.slice((pdfPage - 1) * PDF_POR_PAGINA, pdfPage * PDF_POR_PAGINA);
            return (
              <div>
                <div className="divide-y divide-gray-50">
                  {pdfs.length === 0 ? (
                    <div className="px-6 py-8 text-center text-sm text-gray-400">Nenhum PDF gerado ainda.</div>
                  ) : (
                    pdfsVisiveis.map((pdf) => (
                      <div key={pdf.id} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">📄</span>
                          <div>
                            <p className="text-sm font-medium text-gray-700">{pdf.nome_arquivo}</p>
                            <p className="text-xs text-gray-400">
                              Gerado por <span className="font-medium">{pdf.gerado_por}</span> em {formatarData(pdf.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleBaixarPdf(pdf.id, pdf.nome_arquivo)}
                            className="px-3 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors">
                            ⬇ Baixar
                          </button>
                          <button onClick={() => handleGerarOcorrencias(pdf.nome_arquivo.replace(`.pdf`, '').split('_').pop())}
                            className="px-3 py-1.5 text-xs font-semibold text-purple-600 hover:bg-purple-50 border border-purple-200 rounded-lg transition-colors">
                            📊 Ocorrências
                          </button>
                          {podeExcluir && (
                            <button onClick={() => handleDeletarPdf(pdf.id)}
                              className="px-3 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50 border border-red-200 rounded-lg transition-colors">
                              🗑 Excluir
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {totalPaginas > 1 && (
                  <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
                    <p className="text-xs text-gray-400">{pdfs.length} arquivo{pdfs.length !== 1 ? "s" : ""} · página {pdfPage} de {totalPaginas}</p>
                    <div className="flex gap-2">
                      <button onClick={() => setPdfPage(p => p - 1)} disabled={pdfPage === 1}
                        className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        ← Anterior
                      </button>
                      <button onClick={() => setPdfPage(p => p + 1)} disabled={pdfPage === totalPaginas}
                        className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        Próxima →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

      {/* Botão Flutuante - Voltar ao Topo */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all duration-300 z-50"
          title="Voltar ao topo"
        >
          ⬆
        </button>
      )}
    </div>
  );
}